import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { T } from '../lib/tokens'
import { usePendingItems, useTrashItems } from '../lib/hooks'
import { Sidebar } from '../components/Sidebar'
import { Topbar } from '../components/Topbar'
import { CatBadge, Confidence, TimeAgo, LoadingSpinner } from '../components/ui'
import { reprocessItem } from '../lib/api'

const miniBtn: React.CSSProperties = {
  background: 'transparent', border: `1px solid ${T.border}`, borderRadius: 5,
  color: T.textDim, cursor: 'pointer', fontSize: 11, fontFamily: T.fontMono, padding: '3px 8px',
}

const MODES = [
  { id:'save',     label:'📥 保存のみ' },
  { id:'research', label:'🔍 ウェブ検索補足' },
  { id:'expand',   label:'📖 深掘り・解説' },
  { id:'rewrite',  label:'✏️ 文章を書き直す' },
  { id:'custom',   label:'⚙️ カスタム指示' },
]

export function IngestPage() {
  const { data: items = [], isLoading } = usePendingItems()
  const [selected, setSelected] = useState<string | null>(null)
  const [checked, setChecked] = useState<Set<string>>(new Set())
  const [mode, setMode] = useState('save')
  const [customPrompt, setCustomPrompt] = useState('')
  const [processing, setProcessing] = useState(false)
  const qc = useQueryClient()
  const { mutate: trashItems, isPending: trashing } = useTrashItems()

  const selectedItem = items.find(i => i.id === selected) ?? items[0] ?? null
  const allChecked = items.length > 0 && checked.size === items.length

  function toggleCheck(id: string) {
    setChecked(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }
  function toggleAll() {
    setChecked(allChecked ? new Set() : new Set(items.map(i => i.id)))
  }
  function deleteIds(ids: string[]) {
    if (ids.length === 0) return
    if (!confirm(`${ids.length} 件をゴミ箱に移動しますか？（アーカイブ画面のゴミ箱から復元できます）`)) return
    trashItems(ids, {
      onSuccess: () => {
        setChecked(prev => { const n = new Set(prev); ids.forEach(id => n.delete(id)); return n })
        if (selected && ids.includes(selected)) setSelected(null)
      },
    })
  }

  async function handleReprocess() {
    if (!selectedItem) return
    setProcessing(true)
    try {
      await reprocessItem(selectedItem.id, mode)
      qc.invalidateQueries({ queryKey: ['pending-items'] })
    } finally {
      setProcessing(false)
    }
  }

  return (
    <div style={{ display:'flex',height:'100dvh',background:T.bg,color:T.text,fontFamily:T.font,overflow:'hidden' }}>
      <Sidebar />
      <div style={{ flex:1,display:'flex',flexDirection:'column',overflow:'hidden' }}>
        <Topbar subtitle={`未確認 ${items.length} 件`} />
        <div style={{ flex:1,display:'grid',gridTemplateColumns:'300px 1fr',overflow:'hidden' }}>

          {/* 左: キュー一覧 */}
          <div style={{ borderRight:`1px solid ${T.border}`,overflowY:'auto',background:T.bgPanel }}>
            <div style={{ padding:'12px 14px',borderBottom:`1px solid ${T.border}`,display:'flex',alignItems:'center',gap:8 }}>
              <span style={{ flex:1,fontSize:11,fontFamily:T.fontMono,color:T.textMute,letterSpacing:'0.08em' }}>
                PENDING QUEUE · {items.length}件
              </span>
              {items.length > 0 && (
                <button onClick={toggleAll} style={miniBtn}>{allChecked ? '全解除' : '全選択'}</button>
              )}
              {checked.size > 0 && (
                <button onClick={()=>deleteIds([...checked])} disabled={trashing} style={{ ...miniBtn, color:T.warn, borderColor:T.warn+'60' }}>
                  🗑 削除 ({checked.size})
                </button>
              )}
            </div>
            {isLoading && <div style={{ padding:20 }}><LoadingSpinner /></div>}
            {items.length === 0 && !isLoading && (
              <div style={{ padding:'40px 20px',textAlign:'center',color:T.textMute,fontSize:12 }}>
                未確認のアイテムはありません
              </div>
            )}
            {items.map(item => (
              <div key={item.id} style={{ display:'flex',alignItems:'flex-start',gap:8,padding:'12px 14px',background:selected===item.id||(!selected&&item===items[0])?T.bgHover:'transparent',borderBottom:`1px solid ${T.border}` }}>
                <input
                  type="checkbox"
                  checked={checked.has(item.id)}
                  onChange={()=>toggleCheck(item.id)}
                  style={{ marginTop:3,accentColor:T.accent,cursor:'pointer',flexShrink:0 }}
                />
                <button onClick={()=>setSelected(item.id)} style={{ flex:1,minWidth:0,display:'block',textAlign:'left',background:'transparent',border:'none',padding:0,cursor:'pointer',fontFamily:T.font }}>
                  <div style={{ display:'flex',alignItems:'center',gap:6,marginBottom:5 }}>
                    <span style={{ fontSize:10,fontFamily:T.fontMono,padding:'1px 6px',borderRadius:3,background:item.status==='processing'?T.accentSoft:T.warnSoft,color:item.status==='processing'?'oklch(0.85 0.12 258)':T.warn,border:`1px solid ${item.status==='processing'?T.accentBorder:T.warn+'40'}` }}>
                      {item.status==='processing'?'処理中':'未確認'}
                    </span>
                    <TimeAgo iso={item.created_at} />
                  </div>
                  <div style={{ fontSize:12,color:T.text,fontWeight:500,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>
                    {item.title??item.original_content.slice(0,60)}
                  </div>
                  {item.confidence != null && item.confidence < 0.8 && (
                    <div style={{ fontSize:10,color:T.warn,marginTop:3 }}>⚠️ 要確認（信頼度低）</div>
                  )}
                </button>
                <button onClick={()=>deleteIds([item.id])} disabled={trashing} title="ゴミ箱に移動" style={{ background:'transparent',border:'none',color:T.textMute,cursor:'pointer',fontSize:13,padding:'0 2px',flexShrink:0 }}>🗑</button>
              </div>
            ))}
          </div>

          {/* 右: 編集パネル */}
          <div style={{ overflowY:'auto',padding:'24px 28px' }}>
            {selectedItem ? (
              <div style={{ display:'flex',flexDirection:'column',gap:20,maxWidth:640 }}>
                <div>
                  <div style={{ fontSize:11,fontFamily:T.fontMono,color:T.textMute,marginBottom:10,letterSpacing:'0.08em' }}>元のメモ（原文・変更不可）</div>
                  <div style={{ background:T.bgRaised,border:`1px solid ${T.border}`,borderRadius:7,padding:'12px 14px',fontSize:13,color:T.textDim,lineHeight:1.7,whiteSpace:'pre-wrap' }}>
                    {selectedItem.original_content}
                  </div>
                </div>

                {selectedItem.summary && (
                  <div>
                    <div style={{ fontSize:11,fontFamily:T.fontMono,color:T.textMute,marginBottom:10,letterSpacing:'0.08em' }}>Gemini 要約</div>
                    <div style={{ background:T.bgRaised,border:`1px solid ${T.accentBorder}`,borderRadius:7,padding:'12px 14px',fontSize:13,color:T.text,lineHeight:1.7 }}>
                      {selectedItem.summary}
                    </div>
                  </div>
                )}

                <div>
                  <div style={{ fontSize:11,fontFamily:T.fontMono,color:T.textMute,marginBottom:10,letterSpacing:'0.08em' }}>処理モードを変更して再実行</div>
                  <div style={{ display:'flex',flexDirection:'column',gap:6,marginBottom:12 }}>
                    {MODES.map(m=>(
                      <label key={m.id} style={{ display:'flex',alignItems:'center',gap:10,padding:'8px 12px',borderRadius:6,background:mode===m.id?T.bgActive:T.bgRaised,border:`1px solid ${mode===m.id?T.borderHi:T.border}`,cursor:'pointer' }}>
                        <input type="radio" name="mode" value={m.id} checked={mode===m.id} onChange={()=>setMode(m.id)} style={{ accentColor:T.accent }} />
                        <span style={{ fontSize:13,color:mode===m.id?T.text:T.textDim }}>{m.label}</span>
                      </label>
                    ))}
                  </div>
                  {mode==='custom'&&(
                    <textarea value={customPrompt} onChange={e=>setCustomPrompt(e.target.value)} placeholder="Gemini への指示を入力（例: 英語に翻訳して補足説明を付けて）" style={{ width:'100%',height:80,background:T.bgRaised,border:`1px solid ${T.borderHi}`,borderRadius:6,padding:'10px 12px',color:T.text,fontSize:13,fontFamily:T.font,resize:'vertical',boxSizing:'border-box',outline:'none' }} />
                  )}
                  <button onClick={handleReprocess} disabled={processing} style={{ marginTop:10,padding:'10px 20px',background:T.accent,border:'none',borderRadius:7,color:'#0a0b0e',fontSize:13,fontWeight:600,cursor:processing?'not-allowed':'pointer',opacity:processing?0.6:1,fontFamily:T.font }}>
                    {processing?'処理中…':'↺ 再実行'}
                  </button>
                </div>

                <div style={{ display:'flex',gap:8,flexWrap:'wrap',alignItems:'center' }}>
                  <CatBadge catId={selectedItem.cat_id} />
                  <Confidence value={selectedItem.confidence} />
                  <button onClick={()=>deleteIds([selectedItem.id])} disabled={trashing} style={{ marginLeft:'auto',padding:'6px 12px',borderRadius:6,fontSize:12,cursor:'pointer',background:'transparent',border:`1px solid ${T.warn}60`,color:T.warn,fontFamily:T.font }}>
                    🗑 このアイテムを削除
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ display:'flex',alignItems:'center',justifyContent:'center',height:'100%',color:T.textMute,fontSize:13 }}>
                左のリストからアイテムを選択してください
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
